import { DatadogProvider } from '@datadog/openfeature-browser';
import {
  EvaluationContext,
  OpenFeature,
  Provider,
  ProviderEvents,
  ProviderStatus,
} from '@openfeature/web-sdk';
import { isEqual } from 'lodash-es';

import { env, getConfigValue } from 'utils/environment';
import { LDUser } from 'utils/launchdarkly/user';

import { legacyExcludedKeys, legacyKnownKeys, legacyReadTypes } from './legacy-read-metadata';
import { datadogFlagsSite } from './provider';

export const LEGACY_FLAGS_DOMAIN = 'legacy-launchdarkly';
export const LEGACY_CONTEXT_TIMEOUT_MS = 2000;

type Result = { resolved: false } | { resolved: true; value: any };

// OpenFeature's API-level lifecycle logger is separate from the client logger.
// Server error messages may contain targeting attributes; never propagate them.
export class LegacyDatadogProvider extends DatadogProvider {
  private contextGeneration = 0;
  private completedContext?: EvaluationContext;

  // Provenance recorded at supported lifecycle boundaries, never read from
  // SDK-private stored context/configuration. Older SDK calls can delegate to
  // a newer context update, so only the latest call may publish its context.
  public get readyContext(): EvaluationContext | undefined {
    return this.completedContext;
  }

  private async recordContext(context: EvaluationContext, work: () => Promise<void>) {
    const generation = ++this.contextGeneration;
    this.completedContext = undefined;
    await work();
    if (generation === this.contextGeneration && this.status === ProviderStatus.READY) {
      this.completedContext = context;
    }
  }

  public async initialize(context?: EvaluationContext): Promise<void> {
    try {
      await this.recordContext(context || {}, () => super.initialize(context));
    } catch (_) {
      throw new Error('Legacy flag initialization unavailable');
    }
  }

  public async onContextChange(
    oldContext: EvaluationContext,
    context: EvaluationContext
  ): Promise<void> {
    try {
      await this.recordContext(context, () => super.onContextChange(oldContext, context));
    } catch (_) {
      throw new Error('Legacy flag context unavailable');
    }
  }
}

// Browser 1.4's precomputed evaluator always constructs assignment metadata,
// including an allocationKey property even when its value is absent. OpenFeature
// preserves that public metadata object. The empty non-precomputed DEFAULT has
// neither this property nor a variant. variationType is optional server metadata.
export const hasLegacyAssignment = (details: {
  variant?: string;
  flagMetadata?: object;
}): boolean =>
  details.variant !== undefined ||
  Object.prototype.hasOwnProperty.call(details.flagMetadata || {}, 'allocationKey');

// Report migrated flag evaluations, exposures, and RUM attribution alongside
// the newer Datadog flags. Exposure telemetry includes evaluation context, and
// the SDK's RUM integration can enrich it with RUM user attributes.
export const createLegacyDatadogProvider = (): DatadogProvider => {
  const { rum } = getConfigValue('datadog');
  return new LegacyDatadogProvider({
    applicationId: rum.applicationId,
    clientToken: rum.clientToken,
    site: datadogFlagsSite(rum.dataCenter),
    env: env(),
    enableRumFeatureFlagTracking: true,
    enableFlagEvaluationTracking: true,
    enableExposureLogging: true,
  });
};

const legacySemverContext = (value: unknown): EvaluationContext => {
  if (value === null || value === undefined) {
    return {};
  }
  // LD adds missing minor/patch components before parsing. Keep the original
  // attribute and expose normalized input separately for DD version predicates.
  // Present invalid values match a negated LD version clause; absent ones do not.
  // DD bounds core components to uint64. Source comparands are constrained
  // below this cap by the importer, so saturation preserves every comparison.
  const sdkMaxComponent = '18446744073709551615';
  const component = (part: string) =>
    part.length > sdkMaxComponent.length ||
    (part.length === sdkMaxComponent.length && part > sdkMaxComponent)
      ? sdkMaxComponent
      : part;
  if (typeof value === 'string') {
    let candidate = value;
    for (let attempt = 0; attempt < 3; attempt++) {
      const parsed = /^(0|[1-9][0-9]*)\.(0|[1-9][0-9]*)\.(0|[1-9][0-9]*)(?:-([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?(?:\+([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?$/.exec(
        candidate
      );
      if (
        parsed &&
        parsed[0] === candidate &&
        !(parsed[4] && parsed[4].split('.').some(part => /^0[0-9]+$/.test(part)))
      ) {
        return {
          __ld_semver_state__appShellVersion: 'valid',
          __ld_semver__appShellVersion:
            `${component(parsed[1])}.${component(parsed[2])}.${component(parsed[3])}` +
            (parsed[4] ? `-${parsed[4]}` : '') +
            (parsed[5] ? `+${parsed[5]}` : ''),
        };
      }
      // Match LD's retry insertion exactly, including its non-DOTALL suffix.
      const partial = /^([0-9.]*)(.*)/.exec(candidate)!;
      candidate = `${partial[1]}.0${partial[2]}`;
    }
  }
  return { __ld_semver_state__appShellVersion: 'invalid' };
};

export const buildLegacyEvaluationContext = (user: LDUser): EvaluationContext | undefined => {
  const { key, custom = {}, ...attributes } = user;
  delete attributes.privateAttributeNames;
  if (!key || !custom || typeof custom !== 'object' || Array.isArray(custom)) {
    return undefined;
  }
  // LD resolves built-in attributes before custom attributes. A conflicting
  // flattened name cannot express both meanings; use caller defaults for that context.
  if (
    Object.keys(custom).some(
      name =>
        name === 'targetingKey' ||
        (name !== '__ld_semver__appShellVersion' &&
          name !== '__ld_semver_state__appShellVersion' &&
          name in attributes &&
          !isEqual(custom[name], attributes[name]))
    )
  ) {
    return undefined;
  }
  const context = { ...custom, ...attributes, key, targetingKey: key } as EvaluationContext;
  // Derived predicates belong to the adapter; cached/custom attributes must not
  // be able to supply a stale or conflicting version classification.
  delete context.__ld_semver__appShellVersion;
  delete context.__ld_semver_state__appShellVersion;
  return { ...context, ...legacySemverContext(context.appShellVersion) };
};

/** A deadline never stops the SDK's work; a late READY can still recover. */
const bounded = async <T>(work: Promise<T>, ms: number): Promise<T | undefined> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      work.catch(() => undefined),
      new Promise<undefined>(resolve => {
        timer = setTimeout(() => resolve(undefined), Math.max(0, ms));
      }),
    ]);
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }
};

/**
 * One browser provider for legacy reads. The SDK ignores evaluation-time
 * context, so reconciliation and caller-targeted evaluation share a queue.
 * Fresh reads require the reconciled app context. Previously published DD app
 * answers survive only bounded same-subject reconciliation; no LD fallback.
 */
export class LegacyFlags {
  private provider?: Provider;
  private desired?: EvaluationContext;
  private active?: EvaluationContext;
  private appRevision = 0;
  private operation = 0;
  private requested?: { context?: EvaluationContext; operation: number; revision: number };
  private temporary = false;
  private queue: Promise<unknown> = Promise.resolve();
  private listeners = new Set<() => void>();
  private revision = 0;
  // Only accessed, genuine DD app assignments are published. UI continuity is
  // bounded during same-subject reconciliation; targeted caller reads bypass it.
  private observed = new Map<string, Result>();
  private published = new Map<string, Result>();
  private preserveUntil = 0;
  private preserveTimer?: ReturnType<typeof setTimeout>;
  private client = OpenFeature.getClient(LEGACY_FLAGS_DOMAIN);

  public getRevision = (): number => this.revision;

  public subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    // Catch readiness/configuration changes between render and effect setup.
    listener();
    return () => {
      this.listeners.delete(listener);
    };
  };

  private stopPreserving() {
    clearTimeout(this.preserveTimer);
    this.preserveTimer = undefined;
    this.preserveUntil = 0;
  }

  private preservePublished() {
    if (this.preserveUntil || !this.published.size) {
      return;
    }
    this.preserveUntil = Date.now() + LEGACY_CONTEXT_TIMEOUT_MS;
    this.preserveTimer = setTimeout(() => {
      this.stopPreserving();
      this.published.clear();
      this.notify();
    }, LEGACY_CONTEXT_TIMEOUT_MS);
  }

  private appResult(key: string): Result {
    const fresh = !this.temporary && this.desired && isEqual(this.active, this.desired);
    if (
      fresh &&
      this.provider?.status === ProviderStatus.READY &&
      this.client.providerStatus === ProviderStatus.READY &&
      (!(this.provider instanceof LegacyDatadogProvider) ||
        isEqual(this.provider.readyContext, this.active))
    ) {
      this.stopPreserving();
      const result = this.evaluate(key);
      if (result.resolved) {
        this.published.set(key, result);
      } else {
        this.published.delete(key);
      }
      return result;
    }
    if (this.preserveUntil > Date.now() && this.provider?.status !== ProviderStatus.ERROR) {
      return this.published.get(key) || { resolved: false };
    }
    return { resolved: false };
  }

  private notify = () => {
    let changed = false;
    this.observed.forEach((previous, key) => {
      const result = this.appResult(key);
      if (!isEqual(result, previous)) {
        changed = true;
        this.observed.set(key, result);
      }
    });
    if (changed) {
      ++this.revision;
      this.listeners.forEach(listener => listener());
    }
  };

  private enqueue<T>(work: () => Promise<T>): Promise<T> {
    const next = this.queue.then(work, work);
    this.queue = next.catch(() => undefined);
    return next;
  }

  public async initialize(user: LDUser, provider?: Provider) {
    if (this.provider) {
      return;
    }
    try {
      provider = provider || createLegacyDatadogProvider();
      this.provider = provider;
      this.desired = buildLegacyEvaluationContext(user);
      const context = this.desired;
      const token = ++this.operation;
      this.requested = { context, operation: token, revision: this.appRevision };
      Object.values(ProviderEvents).forEach(event =>
        this.client.addHandler(event, () => {
          if (event === ProviderEvents.Error) {
            this.stopPreserving();
            this.published.clear();
          }
          const requested = this.requested;
          if (
            requested &&
            event === ProviderEvents.Ready &&
            requested.operation === this.operation &&
            requested.revision === this.appRevision &&
            !this.temporary &&
            provider instanceof LegacyDatadogProvider &&
            provider.status === ProviderStatus.READY &&
            isEqual(requested.context, this.desired) &&
            isEqual(requested.context, provider.readyContext)
          ) {
            this.active = requested.context;
          }
          this.notify();
        })
      );
      // Do not log SDK errors: upstream response messages can contain attributes.
      this.client.setLogger({ debug: () => {}, info: () => {}, warn: () => {}, error: () => {} });
      const work = OpenFeature.setProviderAndWait(
        LEGACY_FLAGS_DOMAIN,
        provider,
        context || {}
      ).then(
        () => {
          if (token === this.operation) {
            this.active = context;
            this.notify();
          }
        },
        () => this.notify()
      );
      await bounded(work, LEGACY_CONTEXT_TIMEOUT_MS);
    } catch (_) {
      this.active = undefined;
      this.notify();
    }
  }

  private async reconcile(context: EvaluationContext, budget: number): Promise<boolean> {
    const token = ++this.operation;
    this.requested = { context, operation: token, revision: this.appRevision };
    this.active = undefined;
    this.notify();
    const work = OpenFeature.setContext(LEGACY_FLAGS_DOMAIN, context).then(
      () => {
        if (token === this.operation) {
          this.active = context;
          this.notify();
          return this.provider?.status === ProviderStatus.READY;
        }
        return false;
      },
      () => {
        this.notify();
        return false;
      }
    );
    return (await bounded(work, budget)) === true;
  }

  public update(user: LDUser): Promise<unknown> {
    const context = buildLegacyEvaluationContext(user);
    if (isEqual(context, this.desired)) {
      return Promise.resolve();
    }
    if (!context || context.targetingKey !== this.desired?.targetingKey) {
      this.stopPreserving();
      this.published.clear();
    } else {
      this.preservePublished();
    }
    this.desired = context;
    ++this.appRevision;
    this.notify();
    return this.enqueue(async () => {
      if (context && this.provider && isEqual(context, this.desired)) {
        await this.reconcile(context, LEGACY_CONTEXT_TIMEOUT_MS);
      }
    });
  }

  private evaluate(key: string): Result {
    if (
      legacyExcludedKeys.has(key) ||
      this.provider?.status !== ProviderStatus.READY ||
      this.client.providerStatus !== ProviderStatus.READY ||
      (this.provider instanceof LegacyDatadogProvider &&
        !isEqual(this.provider.readyContext, this.active))
    ) {
      return { resolved: false };
    }
    const declaredType =
      legacyReadTypes[key] || (legacyKnownKeys.includes(key) ? 'boolean' : undefined);
    // CMS keys can be any native type. Use public typed evaluations to discover
    // genuine assignments. The provider reports successful evaluations through its hooks.
    const types = declaredType ? [declaredType] : ['boolean', 'number', 'string', 'object'];
    for (const type of types) {
      try {
        const details =
          type === 'number'
            ? this.client.getNumberDetails(key, 0)
            : type === 'string'
            ? this.client.getStringDetails(key, '')
            : type === 'object'
            ? this.client.getObjectDetails(key, {})
            : this.client.getBooleanDetails(key, false);
        // Empty configuration placeholders have no public assignment signal.
        if (
          !details.errorCode &&
          details.reason !== 'ERROR' &&
          hasLegacyAssignment(details) &&
          (type === 'object' ? typeof details.value === 'object' : typeof details.value === type)
        ) {
          // DD JSON variants wrap this legacy array as { value: [...] }.
          // Only this audited key is decoded; native arrays remain compatible.
          if (key === 'supported-card-brands-variations') {
            const value = Array.isArray(details.value)
              ? details.value
              : (details.value as { value?: unknown } | null)?.value;
            return Array.isArray(value) ? { resolved: true, value } : { resolved: false };
          }
          return { resolved: true, value: details.value };
        }
      } catch (_) {
        // A throwing/type-mismatched resolver is not an assignment.
      }
    }
    return { resolved: false };
  }

  public readResult = (key: string): Result => {
    if (legacyExcludedKeys.has(key)) {
      return { resolved: false };
    }
    const result = this.appResult(key);
    if (!this.observed.has(key)) {
      this.observed.set(key, result);
    }
    return result;
  };

  public read = (key: string, fallback: any): any => {
    const result = this.readResult(key);
    return result.resolved ? result.value : fallback;
  };

  public async readForUser(key: string, user: LDUser): Promise<Result> {
    const context = buildLegacyEvaluationContext(user);
    const revision = this.appRevision;
    const deadline = Date.now() + LEGACY_CONTEXT_TIMEOUT_MS;
    if (!context || !this.provider || legacyExcludedKeys.has(key)) {
      return { resolved: false };
    }
    // Include queue time in the caller's budget. A hung previous request must
    // not leave this caller waiting, nor evaluate an expired queued request.
    let resolveCaller: (result: Result) => void = () => {};
    const caller = new Promise<Result>(resolve => {
      resolveCaller = resolve;
    });
    this.enqueue(
      async (): Promise<void> => {
        if (Date.now() >= deadline || revision !== this.appRevision) {
          resolveCaller({ resolved: false });
          return;
        }
        this.preservePublished();
        this.temporary = true;
        this.notify();
        try {
          const ready = await this.reconcile(context, deadline - Date.now());
          if (ready && revision === this.appRevision && Date.now() < deadline) {
            resolveCaller(this.evaluate(key));
            return;
          }
          resolveCaller({ resolved: false });
        } finally {
          // Restoration supersedes a timed-out SDK context change. Sync readers
          // retain only bounded published app answers until correct restoration.
          if (this.desired) {
            await this.reconcile(this.desired, LEGACY_CONTEXT_TIMEOUT_MS);
          }
          this.temporary = false;
          this.notify();
        }
      }
    );
    return (await bounded(caller, LEGACY_CONTEXT_TIMEOUT_MS)) || { resolved: false };
  }
}

export const legacyFlags = new LegacyFlags();
